import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import { ILedgerEntry, ILedgerEntryByDate } from 'types';

/**
 * Groups ledger entries into buckets keyed by the entry creation date.
 *
 * @param entries Ledger entries to group.
 * @returns A map of date key (`MM-DD-YYYY`) to the entries created on that date.
 */
const groupLedgerEntriesByDate = (entries: ILedgerEntry[]) => groupBy(entries, (entry) => dayjs(entry.createdAt).format('MM-DD-YYYY'));

/**
 * Aggregates ledger entries into daily totals.
 *
 * For each day (keyed by `MM-DD-YYYY`), this returns a single record with:
 * - `date`: the day bucket key
 * - `amount`: the sum of `amount` across all entries in that day (in cents)
 *
 * @param ledgerEntries Ledger entries to aggregate.
 * @returns Daily totals, grouped by date.
 */
export const getDailyLedgerEntries = (ledgerEntries: ILedgerEntry[]): ILedgerEntryByDate[] => {
  const ledgerEntriesByDay = groupLedgerEntriesByDate(ledgerEntries);
  return Object.values(ledgerEntriesByDay).map((entries: ILedgerEntry[]) => ({ date: dayjs(entries[0].createdAt).format('MM-DD-YYYY'), amount: sumBy(entries, 'amount') }));
};
